/**
 * MKS-384 — the "N questions waiting" marker of the task detail slide-over.
 *
 * With two Factory™ questions pending on one task, neither the thread nor the
 * Slack DM used to say which question anything belonged to. This marker is the
 * LIVE numbering: `Q<n>` here always means what `Q<n>:` means in a reply,
 * because both resolve the same server-side list
 * (`GET /api/tasks/:taskId/pending-questions`) rather than a number baked into
 * a stored comment.
 *
 * Mounted by TaskDetailSlideOver only for an internal reader on an instance
 * with the robot add-on, so the extra read never fires for anybody else (the
 * `TriagerProposalSection` precedent).
 * Renders nothing at all when the task has no pending question — which is every
 * task almost all of the time.
 *
 * MKS-516 — the list also carries the staging review and rework prompts (no
 * `Q<n>`, so a kind tag instead) and, for each item the viewer may answer, the
 * answer controls. Everyone else sees the same list read-only.
 */
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { usePendingTaskQuestions } from '../../hooks/usePendingTaskQuestions';
import { PendingRequestAnswerControls } from './PendingRequestAnswerControls';

export interface PendingQuestionsSectionProps {
  taskId: number;
  /** Scrolls the thread to that question's own comment and flashes it. */
  onJumpToComment: (commentId: number) => void;
}

export function PendingQuestionsSection({ taskId, onJumpToComment }: PendingQuestionsSectionProps) {
  const { data } = usePendingTaskQuestions(taskId, true);
  const questions = data ?? [];
  // Nothing pending (and every state before the read lands) renders nothing:
  // a marker that flickers "0 questions waiting" would be worse than none.
  if (questions.length === 0) return null;

  return (
    <div
      className="rounded-md border border-red-200 border-l-4 border-l-red-500 bg-red-50 px-3 py-2"
      data-testid="task-detail-pending-questions"
      role="status"
    >
      <div className="flex items-center gap-1.5 text-sm font-semibold text-red-700 mb-1">
        <ExclamationCircleIcon className="w-5 h-5" aria-hidden="true" />
        {questions.length === 1
          ? '1 question waiting for an answer'
          : `${questions.length} questions waiting for an answer`}
      </div>
      <ul className="space-y-1">
        {questions.map((question) => (
          <li key={question.requestId} className="text-sm text-red-900">
            {question.ordinal === null ? (
              <span className="font-medium">
                {question.kind === 'ui_review' ? 'Staging review' : 'Rework scope'}
              </span>
            ) : (
              <span className="font-mono font-medium">Q{question.ordinal}</span>
            )}
            {': '}
            {question.commentId === null ? (
              <span>{question.title}</span>
            ) : (
              <button
                type="button"
                className="text-red-800 underline decoration-red-300 hover:decoration-red-800 text-left"
                onClick={() => onJumpToComment(question.commentId as number)}
              >
                {question.title}
              </button>
            )}
            {question.canAnswer === true && (
              <PendingRequestAnswerControls taskId={taskId} item={question} />
            )}
          </li>
        ))}
      </ul>
      {questions.some((q) => q.kind === undefined || q.kind === 'question') && (
        <p className="mt-1 text-xs text-red-800">
          To answer, write a comment that is exactly one of the options, or start it with the tag
          of the question you are answering (for example <span className="font-mono">Q1:</span>).
          Any other comment stays an ordinary comment.
        </p>
      )}
    </div>
  );
}
